import { useState, type FormEvent } from "react";
import { messageOf } from "../api/http";
import type { ProviderCheck, ProviderRefusal, ProviderRow, Setup } from "../api/sources";
import { runPagePath } from "../runId";
import { Chip, Field, Notice } from "./chrome";
import { whenLabel } from "./RunsPage";
import { SetupTrail, inFirstRun } from "./SetupTrail";
import { DESTINATIONS } from "./routes";

/** A refusal's kind in words: the three the server passes on (#1415). */
const REFUSAL_KINDS: Record<"quota" | "auth" | "model", string> = {
  quota: "No balance or quota",
  auth: "Credentials refused",
  model: "Model not served",
};

/**
 * The model provider (#422): a BYO OpenAI-compatible endpoint. The key is sent
 * once and never comes back; the check reads the provider's model list and
 * invokes nothing. A workspace with none set sees why its first run cannot start.
 */
export function Provider({
  provider,
  lastRefusal = null,
  error,
  onSet,
  onCheck,
  canEdit,
  canCheck,
  setup,
}: {
  provider: ProviderRow | null | undefined;
  /**
   * When this provider last refused an investigation (#1415), or null. History, not a state:
   * the check below does not clear it, and the status chip does not read it.
   */
  lastRefusal?: ProviderRefusal | null;
  /** What is left before the first investigation, or null while unread (#805). */
  setup: Setup | null;
  error: string | null;
  onSet: (body: {
    provider: "openai" | "litellm";
    api_base: string;
    api_key: string;
    high_model_id: string;
    low_model_id: string | null;
  }) => Promise<ProviderRow>;
  onCheck: () => Promise<ProviderCheck>;
  canEdit: boolean;
  canCheck: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [kind, setKind] = useState<"openai" | "litellm">("openai");
  const [apiBase, setApiBase] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [high, setHigh] = useState("");
  const [low, setLow] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [checked, setChecked] = useState<ProviderCheck | null>(null);
  const [saved, setSaved] = useState(false);
  const form = canEdit && (editing || provider === null);
  const modelsAvailable =
    checked !== null && provider != null && provider.has_key &&
    [provider.high_model_id, provider.low_model_id]
      .filter((id): id is string => Boolean(id))
      .every((id) => checked.found[id] === true);
  const status = error ? "Unavailable" : failure ? "Check or save failed" :
    busy ? "Working…" : checked ? modelsAvailable ? "Models available" : "Models unavailable" : "Not checked";
  const tone = error || failure ? "bad" : busy || checked === null ? undefined : modelsAvailable ? "good" : "bad";
  const investigationsPath = DESTINATIONS.find((destination) => destination.id === "runs")?.path ?? "/";

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFailure(null);
    setChecked(null);
    setSaved(false);
    setBusy(true);
    try {
      await onSet({
        provider: kind,
        api_base: apiBase,
        api_key: apiKey,
        high_model_id: high,
        low_model_id: low.trim() === "" ? null : low.trim(),
      });
      setApiKey("");
      setEditing(false);
      setSaved(true);
      setChecked(null);
    } catch (refusal) {
      setFailure(messageOf(refusal));
    } finally {
      setBusy(false);
    }
  };
  const check = async () => {
    setFailure(null);
    setChecked(null);
    setSaved(false);
    setBusy(true);
    try {
      setChecked(await onCheck());
    } catch (refusal) {
      setFailure(messageOf(refusal));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel setup-provider" data-testid="provider" id="provider">
      <div className="panel__head">
        <h2>Model provider</h2>
        {provider === undefined ? null : provider === null ? (
          <Chip tone="bad">none set</Chip>
        ) : (
          <Chip tone={tone}>{status}</Chip>
        )}
      </div>
      <div className="panel__body stack">
        <SetupTrail current="provider" setup={setup} />
        <p className="muted">
          Investigations use a model you bring: an OpenAI-compatible endpoint and its key. The
          key is stored sealed under this workspace and never shown again. Without one, no
          investigation can start.
        </p>
        {error === null ? null : <Notice tone="bad">{error} Reload this page to read the provider again.</Notice>}
        {!canEdit ? <p className="muted">A workspace administrator can configure the provider through a direct workspace membership. Your current access does not allow credential changes.</p> : null}
        {saved ? <Notice>Provider saved. The key is sealed and will not be shown again. Check the connection to verify the configured models.</Notice> : null}
        {failure === null ? null : <Notice tone="bad">{failure}</Notice>}
        {provider === undefined && error === null ? (
          <p className="muted" role="status">
            Reading the provider…
          </p>
        ) : null}
        {provider ? (
          <dl className="setup-provider-details">
            <dt>Endpoint</dt>
            <dd>
              <code className="break">{provider.api_base}</code>
            </dd>
            <dt>Orchestrator model</dt>
            <dd>{provider.high_model_id}</dd>
            <dt>Specialist model</dt>
            <dd>{provider.low_model_id ?? <span className="muted">same as the orchestrator</span>}</dd>
            <dt>Key</dt>
            <dd>{provider.has_key ? "sealed" : <span className="muted">none</span>}</dd>
          </dl>
        ) : null}
        {provider && lastRefusal ? (
          <dl className="setup-provider-details" data-testid="provider-last-refused">
            <dt>Last refused</dt>
            <dd className="stack stack--tight">
              <p>
                <time dateTime={lastRefusal.recorded_at}>{whenLabel(lastRefusal.recorded_at)}</time>
                {" · "}
                {lastRefusal.kind === null ? "Refused" : REFUSAL_KINDS[lastRefusal.kind]}
              </p>
              <p>{lastRefusal.sentence}</p>
              <p>
                <a className="link" href={runPagePath(lastRefusal.run_id)}>
                  Open the investigation it stopped
                </a>
              </p>
            </dd>
          </dl>
        ) : null}
        {checked === null || error !== null || failure !== null ? null : (
          <Notice tone={modelsAvailable ? "good" : "bad"}>
            The provider lists {checked.models} models.{" "}
            {Object.entries(checked.found)
              .map(([model, found]) => {
                const note = checked.notes?.[model];
                return `${model}: ${found ? "found" : "not found"}${note ? ` (${note})` : ""}`;
              })
              .join("; ")}
            .
          </Notice>
        )}
        {form ? (
          <form onSubmit={submit} aria-busy={busy} className="stack stack--tight">
            <p className="field">
              <label htmlFor="provider-kind" className="field__label">
                Kind
              </label>
              <select
                id="provider-kind"
                className="input"
                value={kind}
                onChange={(event) => setKind(event.target.value === "litellm" ? "litellm" : "openai")}
              >
                <option value="openai">OpenAI-compatible</option>
                <option value="litellm">LiteLLM proxy</option>
              </select>
            </p>
            <Field
              id="provider-base"
              label="Base URL"
              type="url"
              required
              placeholder="https://api.example.com/v1"
              value={apiBase}
              onChange={(event) => setApiBase(event.target.value)}
            />
            <Field
              id="provider-key"
              label="API key"
              type="password"
              autoComplete="off"
              required
              hint="Sent once, sealed at rest, never shown again."
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
            />
            <Field
              id="provider-high"
              label="Orchestrator model"
              required
              placeholder="the model id the provider lists"
              value={high}
              onChange={(event) => setHigh(event.target.value)}
            />
            <Field
              id="provider-low"
              label="Specialist model (optional)"
              hint="A smaller model for specialists, chat and compaction; empty means the orchestrator's."
              value={low}
              onChange={(event) => setLow(event.target.value)}
            />
            <div className="actions">
              <button type="submit" className="btn btn--primary" disabled={busy}>
                {busy ? "Saving…" : "Save provider"}
              </button>
              {provider ? (
                <button type="button" className="link link--muted" onClick={() => { setApiKey(""); setEditing(false); }}>
                  Cancel
                </button>
              ) : null}
            </div>
          </form>
        ) : provider ? (
          <div className="actions">
            {canCheck ? <button type="button" className="btn btn--small" disabled={busy} onClick={() => void check()}>
              {busy ? "Checking…" : "Check the connection"}
            </button> : null}
            {canEdit ? <button
              type="button"
              className="btn btn--small"
              disabled={busy}
              onClick={() => {
                if (provider) {
                  setKind(provider.provider);
                  setApiBase(provider.api_base);
                  setHigh(provider.high_model_id);
                  setLow(provider.low_model_id ?? "");
                }
                setSaved(false);
                setEditing(true);
              }}
            >
              Replace the key or the models
            </button> : null}
          </div>
        ) : null}
        {provider && !form && inFirstRun(setup) ? (
          <div className="setup-next">
            <p className="muted">A connected account, confirmed alert source and model provider prepare your workspace for its first investigation.</p>
            <a className="btn" href={investigationsPath}>View investigations</a>
          </div>
        ) : null}
      </div>
    </section>
  );
}
